import api from './api';

// Description: Generate agreement for a contract
// Endpoint: POST /clc/agreements
// Request: { contractId: number, isTestSubmission: boolean, isSendWelcome: boolean, isSendWelcomeEmailDraft: boolean, isAttachJointMemberForm: boolean, isWithoutII: boolean, isWithoutIIGuaranteedReservation: boolean }
// Response: { success: boolean, message: string }
// Note: whether the contract uses the form-of-surrender workflow is derived server-side
// from the contract's form_of_surrenders rows - no client-side flag to pass.
export const generateAgreement = async (
  contractId: number,
  isTestSubmission: boolean = false,
  isSendWelcomeEmail: boolean = false,
  isSendWelcomeEmailDraft: boolean = false,
  isAttachJointMemberForm: boolean = false,
  isWithoutII: boolean = false,
  isWithoutIIGuaranteedReservation: boolean = false
) => {
  try {
    const response = await api.post('/api/clc/agreements', {
      contractId,
      isTestSubmission,
      isSendWelcomeEmail,
      isWithoutII,
      isWithoutIIGuaranteedReservation,
    });
    console.log("Agreeements:", response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error generating agreement:', error);

    // Return the error response data if available
    if (error.response?.data) {
      return error.response.data;
    }

    // Provide more detailed error information
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Server response error:', error.response.data);
      console.error('Status code:', error.response.status);
      return {
        success: false,
        message: error.response.data?.message || `Server error (${error.response.status}): Failed to generate agreement`
      };
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
      return {
        success: false,
        message: 'No response from server. Please try again later.'
      };
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Request setup error:', error.message);
      return {
        success: false,
        message: error.message || 'Failed to generate agreement'
      };
    }
  }
};


// Description: Get all agreements status
// Endpoint: GET /clc/agreements
// Response: { success: boolean, agreements: Array<{ contractId: number, adobeStatus: string }> }
export const getAgreementsStatus = async () => {
  try {
    const response = await api.get('/api/clc/agreements');
    return response.data;
  } catch (error: any) {
    console.error('Error fetching agreements status:', error);

    // Return the error response data if available
    if (error.response?.data) {
      return error.response.data;
    }

    throw new Error(error?.response?.data?.message || error.message);
  }
};

// Description: Archive an agreement
// Endpoint: POST /clc/agreements/archive
// Request: { contractId: number }
// Response: { success: boolean, message: string }
export const archiveAgreement = async (contractId: number) => {
  try {
    const response = await api.post('/api/clc/agreements/archive', { contractId });
    return response.data;
  } catch (error: any) {
    console.error('Error archiving agreement:', error);

    // Return the error response data if available
    if (error.response?.data) {
      return error.response.data;
    }

    throw new Error(error?.response?.data?.message || error.message);
  }
};

// Description: Delete an agreement
// Endpoint: DELETE /clc/agreements/{contractId}
// Response: { success: boolean, message: string }
export const deleteAgreement = async (contractId: number) => {
  try {
    const response = await api.delete(`/api/clc/agreements/${contractId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error deleting agreement:', error);

    // Return the error response data if available
    if (error.response?.data) {
      return error.response.data;
    }

    throw new Error(error?.response?.data?.message || error.message);
  }
};

// Description: Cancel an agreement
// Endpoint: POST /clc/agreements/cancel
// Request: { contractId: number }
// Response: { success: boolean, message: string }
export const cancelAgreement = async (contractId: number) => {
  try {
    const response = await api.post('/api/clc/agreements/cancel', { contractId });
    return response.data;
  } catch (error: any) {
    console.error('Error cancelling agreement:', error);

    // Return the error response data if available
    if (error.response?.data) {
      return error.response.data;
    }

    throw new Error(error?.response?.data?.message || error.message);
  }
};